/* eslint-disable no-magic-numbers */
import UtilCookie from '@cewe-designsystem/util_cookie';
export default class UtilCref {
    constructor(keyAccount) {
        /**
         * Name of the cookie to be used for storing the CREF value.
         */
        this.cookieName = 'cref';
        /**
         * Lifetime (days) of the cookie to be used for storing the CREF value.
         */
        this.cookieLifetime = 30;
        /**
         * Name of the URL parameter to be used for the CREF value.
         */
        this.paramName = 'cref';
        /**
         * The character used for delimiting the parts of a CREF value.
         */
        this.delimiter = '_';
        /**
         * The character used for filling unset places in the CREF value.
         */
        this.unsetCharacter = 'x';
        /**
         * The CREF value to be used if there is no CREF value in the cookie.
         */
        this.defaultValue = 'CREF_LOCAL_NO_COOKIE_PLACEHOLDER';
        /**
         * The placeholder used for replacing the CREF value.
         */
        this.placeholder = /%%CREFPLACEHOLDER%%/g;
        /**
         * The key account of the current site.
         */
        this.keyAccount = '';
        /**
         * Check if the CREF is already initialized.
         */
        this.isInitialized = false;
        this.keyAccount = keyAccount || '';
        if (!this.keyAccount) {
            console.error('KeyAccount is not set. This will cause problems when determining the CREF.');
        }
        this.utilCookie = new UtilCookie();
    }
    /**
     * Set the Cookie Util.
     */
    setCookieUtil(utilCookie) {
        this.utilCookie = utilCookie;
    }
    /**
     * Generate the CREF and store it in the cookies.
     */
    initCref() {
        if (this.isInitialized) {
            // The CREF is already initialized.
            return;
        }
        if (!navigator.cookieEnabled) {
            // @TODO: Migrate to "util_logger".
            console.log('Cookies not enabled');
            return;
        }
        const crefFromCookie = this.getCrefFromCookies();
        const crefFromUrlParam = this.getCrefFromURLParameters();
        if (crefFromCookie && this.isScidCref(crefFromCookie) && !this.isScidCref(crefFromUrlParam)) {
            // If the CREF value in the cookie is an affiliate CREF (starts with "scid"), the cookie's CREF value has to be used.
            // It is only possible to override an existing SCID CREF with another SCID CREF.
            return;
        }
        let crefValue = crefFromUrlParam || crefFromCookie;
        // Try to get the CREF based on the referrer
        if (!crefValue) {
            const referer = document.referrer.split('?')[0];
            if (referer.includes('google')) {
                crefValue = ['om', 'seo', 'goo', this.unsetCharacter, this.keyAccount, this.unsetCharacter].join(this.delimiter);
            }
            else if (referer.includes('bing')) {
                crefValue = ['om', 'seo', 'bing', this.unsetCharacter, this.keyAccount, this.unsetCharacter].join(this.delimiter);
            }
            else if (referer.includes('yahoo')) {
                crefValue = ['om', 'seo', 'yah', this.unsetCharacter, this.keyAccount, this.unsetCharacter].join(this.delimiter);
            }
        }
        if (!crefValue) {
            // CREF is empty. So, a new (empty) CREF is generated.
            crefValue = [
                this.unsetCharacter,
                this.unsetCharacter,
                this.unsetCharacter,
                this.unsetCharacter,
                this.keyAccount,
                this.unsetCharacter,
            ].join(this.delimiter);
        }
        crefValue = this.finalizeCref(crefValue);
        if (crefValue !== crefFromCookie) {
            // The CREF has been changed so that it should be updated.
            this.utilCookie.setCookie(this.cookieName, crefValue, '/', this.cookieLifetime);
        }
        this.isInitialized = true;
    }
    /**
     * Finalize the CREF by filling the unset positions.
     */
    finalizeCref(cref) {
        if (this.isScidCref(cref)) {
            // Do not expand the CREF for CREFs containing a SCID.
            return cref;
        }
        const crefSplit = cref.split(this.delimiter);
        // Fill up CREF with 'x' to match at least 6 positions.
        if (crefSplit.length < 6) {
            Array.prototype.push.apply(crefSplit, Array(6 - crefSplit.length).fill(this.unsetCharacter));
        }
        // Check 5th position.
        if (crefSplit[4] === this.unsetCharacter) {
            crefSplit[4] = this.keyAccount || this.unsetCharacter;
        }
        // Check 6th position. In special cases, a "cwo" is inserted at this position.
        if (window.targetGroupHandler && window.targetGroupHandler.isActive && window.targetGroupHandler.isActive()) {
            if (crefSplit[5] === this.unsetCharacter) {
                crefSplit[5] = 'cwo';
            }
            else if (!crefSplit[5].endsWith('cwo')) {
                crefSplit[5] += '-cwo';
            }
        }
        // Check if the (probably) existing hash is still valid.
        if (crefSplit.length === 7) {
            // The CREF already has a hash.
            if (!this.isValidHash(crefSplit[6])) {
                // The already existing hash is invalid so that it should be removed.
                crefSplit.splice(6, 1);
            }
        }
        // Add hash if necessary.
        return this.appendHash(crefSplit.join(this.delimiter));
    }
    /**
     * Add a hash to the 7th position of the CREF.
     */
    appendHash(cref) {
        if (cref.split(this.delimiter).length !== 6) {
            return cref;
        }
        // Add hash.
        return [cref, this.keyAccount.padStart(5, '0') + '-' + this.getNonce(13)].join(this.delimiter);
    }
    /**
     * Check if a hash is valid.
     */
    isValidHash(hash) {
        return !!hash.match(/^\d{5}-[a-zA-Z\d]{13}$/);
    }
    /**
     * Check if a CREF contains a SCID.
     */
    isScidCref(cref) {
        if (!cref) {
            return false;
        }
        const pattern = new RegExp('^scid' + this.delimiter + '[0-9]+.*$', 'i');
        return pattern.test(cref);
    }
    /**
     * Get unique nonce with custom length (A-Za-z0-9).
     * Please note that this nonce is not cryptographically strong. If you need to, please create a RFC-compliant UUID.
     */
    getNonce(length = 13) {
        const dict = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let nonce = '';
        for (let i = 0; i < length; i++) {
            nonce += dict.charAt(Math.floor(Math.random() * dict.length));
        }
        return nonce;
    }
    /**
     * Replace the CREF placeholder in a string.
     */
    replacePlaceholder(input) {
        return input.replace(this.placeholder, this.getCref());
    }
    /**
     * Try to get the CREF value from the cookie.
     * If no CREF value is found in the cookies, the default CREF value is returned.
     */
    getCref() {
        this.initCref();
        return this.getCrefFromCookies() || this.defaultValue;
    }
    /**
     * Try to get the SCID from the CREF value.
     */
    getScid() {
        const cref = this.getCref();
        const pattern = new RegExp('^scid' + this.delimiter + '([0-9]+).*$', 'i');
        const matches = cref.match(pattern);
        if (matches && matches[1]) {
            return matches[1];
        }
        else {
            return undefined;
        }
    }
    /**
     * Try to get the shortened CREF value from the cookie.
     * If no CREF value is found in the cookies, the default CREF value is returned.
     *
     * @return {string}
     */
    getShortCref() {
        const crefValueSplit = this.getCref().split(this.delimiter).reverse();
        if (crefValueSplit.length === 7) {
            crefValueSplit.shift();
        }
        return crefValueSplit.reverse().join(this.delimiter);
    }
    /**
     * Try to get the CREF from the cookies.
     */
    getCrefFromCookies() {
        return this.utilCookie.getCookie(this.cookieName);
    }
    /**
     * Try to get the CREF from the URL parameters.
     */
    getCrefFromURLParameters() {
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.get(this.paramName);
    }
}
